import { useReactiveVar } from '@apollo/client';
import { useNavigate } from '@hulu/react-router-dom';
import { useCallback } from 'react';

import { isBaymaxEnabledVar, RoutePath, selectedAdAccountIdVar } from '../../../constants';
import { HelpSiteCookieHelper } from '../../../utils/user/interfaces';

type UsePostLoginHeaderNavigate = {
  handleClickToCampaigns: VoidFunction;
  handleClickToReports: (disabled: boolean) => void;
  handleClickToAdGallery: (disabled: boolean) => void;
  handleAdAccountDropdownClick: (id: string) => void;
  handleClickToHamhub: (link: string) => void;
};

const usePostLoginHeaderNavigate = (pathname = ''): UsePostLoginHeaderNavigate => {
  const navigate = useNavigate();
  const selectedAdAccountId = useReactiveVar(selectedAdAccountIdVar);
  const isBaymaxEnabled = useReactiveVar(isBaymaxEnabledVar);

  const handleClickToCampaigns = (): void => {
    const campaignsPath = `/${selectedAdAccountId}${RoutePath.CAMPAIGNS}`;

    if (pathname !== campaignsPath) {
      navigate(campaignsPath);
    }
  };

  const handleClickToReports = (disabled: boolean): void => {
    const isMixedReports = Boolean(isBaymaxEnabled && selectedAdAccountId);
    const mixedReportsPath = isMixedReports ? '/mixed/self-serve' : '';
    const adAccountId = selectedAdAccountId ? `/${selectedAdAccountId}` : '';
    const reportsPath = `${RoutePath.REPORTS}${mixedReportsPath}${adAccountId}`;

    if (!pathname.includes(RoutePath.REPORTS) && !disabled) {
      navigate(reportsPath);
    }
  };

  const handleClickToAdGallery = (disabled: boolean): void => {
    if (pathname !== RoutePath.AD_GALLERY && !disabled) {
      navigate(RoutePath.AD_GALLERY);
    }
  };

  const handleClickToHamhub = async (link: string): Promise<void> => {
    // Make sure hamhub cookies are set before following hamhub link
    try {
      await HelpSiteCookieHelper.setHelpSiteCookieForNonAdmin(true);
    } finally {
      window.open(link, '_blank');
    }
  };

  const handleAdAccountDropdownClick = useCallback<(id: string) => void>(
    (id): void => {
      const [, account] = pathname.split('/');
      const newCampaignPath = `/${id}/${pathname.split('/').slice(2).join('/')}`;

      if (account !== 'account') {
        navigate(newCampaignPath);
      }
    },
    [navigate, pathname]
  );

  return {
    handleAdAccountDropdownClick,
    handleClickToAdGallery,
    handleClickToReports,
    handleClickToCampaigns,
    handleClickToHamhub,
  };
};

export default usePostLoginHeaderNavigate;
